import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { FileText, Plus, RefreshCw } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  ServerDataTable,
  TableSkeleton,
} from "@/components/ui/server-data-table";
import { useServerTable } from "@/components/ui/use-server-table";
import { useInvoiceColumns } from "@/components/sales-invoices/invoice-columns";
import { InvoiceListFilters } from "@/components/sales-invoices/InvoiceListFilters";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { useSalesInvoices } from "@/lib/api/sales-invoices.queries";
import { useActiveCompany } from "@/lib/company-context";
import type { SalesInvoiceStatusFilter } from "@/lib/api/sales-invoices";

export const Route = createFileRoute("/companies/$companyId/facturas/")({
  component: FacturasIndexPage,
});

function FacturasIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <FacturasIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function FacturasIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const navigate = useNavigate();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const [q, setQ] = useState("");
  const [status, setStatus] = useState<SalesInvoiceStatusFilter>("all");
  const [periodFrom, setPeriodFrom] = useState("");
  const [periodTo, setPeriodTo] = useState("");

  const columns = useInvoiceColumns();
  const table = useServerTable({
    defaultSort: { id: "emitDate", desc: true },
    defaultPageSize: 50,
    filterKey: JSON.stringify({ q, status, periodFrom, periodTo }),
  });

  const invoicesQuery = useSalesInvoices(companyId, {
    ...(q ? { q } : {}),
    status,
    ...(periodFrom ? { periodFrom } : {}),
    ...(periodTo ? { periodTo } : {}),
    ...table.params,
  });

  const hasFilters =
    q.length > 0 || status !== "all" || !!periodFrom || !!periodTo;
  const pagination = invoicesQuery.data?.pagination;

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="factura_venta" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            {t("invoices.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("invoices.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("invoices.list.subtitle")}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={() => void invoicesQuery.refetch()}
            disabled={invoicesQuery.isFetching}
          >
            <RefreshCw className={invoicesQuery.isFetching ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
            {t("invoices.list.refresh")}
          </Button>
          <Button asChild>
            <Link to="/companies/$companyId/facturas/new" params={{ companyId }}>
              <Plus className="h-4 w-4" />
              {t("invoices.list.new")}
            </Link>
          </Button>
        </div>
      </div>

      <InvoiceListFilters
        q={q}
        onQChange={setQ}
        status={status}
        onStatusChange={setStatus}
        periodFrom={periodFrom}
        onPeriodFromChange={setPeriodFrom}
        periodTo={periodTo}
        onPeriodToChange={setPeriodTo}
      />

      {invoicesQuery.isPending ? (
        <TableSkeleton columns={columns} />
      ) : invoicesQuery.isError ? (
        <ErrorState
          title={t("invoices.list.error_title")}
          description={t("invoices.list.error_body")}
          onRetry={() => void invoicesQuery.refetch()}
          retryLabel={t("common.retry")}
          error={invoicesQuery.error}
        />
      ) : invoicesQuery.data.invoices.length === 0 ? (
        <EmptyState companyId={companyId} hasFilters={hasFilters} />
      ) : (
        <ServerDataTable
          columns={columns}
          data={invoicesQuery.data.invoices}
          pageCount={pagination?.totalPages ?? 1}
          rowCount={pagination?.total ?? 0}
          sorting={table.sorting}
          onSortingChange={table.onSortingChange}
          pagination={table.pagination}
          onPaginationChange={table.onPaginationChange}
          getRowId={(invoice) => invoice.id}
          onRowClick={(invoice) =>
            void navigate({
              to: "/companies/$companyId/facturas/$invoiceId",
              params: { companyId, invoiceId: invoice.id },
            })
          }
          isFetching={invoicesQuery.isFetching}
        />
      )}
    </div>
  );
}

function EmptyState({ companyId, hasFilters }: { companyId: string; hasFilters: boolean }) {
  const { t } = useTranslation();
  if (hasFilters) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
          <h2 className="text-lg font-semibold">{t("invoices.list.empty_search_title")}</h2>
          <p className="text-sm text-muted-foreground">{t("invoices.list.empty_search_body")}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
          <FileText className="h-8 w-8 text-primary" />
        </div>
        <div className="max-w-sm space-y-1">
          <h2 className="text-lg font-semibold">{t("invoices.list.empty_title")}</h2>
          <p className="text-sm text-muted-foreground">{t("invoices.list.empty_body")}</p>
        </div>
        <Button asChild>
          <Link to="/companies/$companyId/facturas/new" params={{ companyId }}>
            <Plus className="h-4 w-4" />
            {t("invoices.list.new")}
          </Link>
        </Button>
      </CardContent>
    </Card>
  );
}
